'use client'

import { useState, useEffect } from 'react'
import { User, Property, Application } from '@/types'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { getApiUrl } from '@/lib/apiUrl'



interface Report {
  id: string
  reporter_id: string
  reported_user_id: string
  content_type: 'property' | 'user' | 'application' | 'message' | 'other'
  content_id?: string
  report_reason: 'inappropriate' | 'spam' | 'fraud' | 'harassment' | 'fake_listing' | 'other'
  description: string
  evidence?: string
  status: 'pending' | 'under_review' | 'resolved' | 'dismissed'
  admin_notes?: string
  action_taken?: string
  created_at: string
  updated_at: string
  reporter?: User
  reported_user?: User
  property?: Property
  application?: Application
}

interface ContentModerationProps {
  onUpdate: () => void
}

export default function ContentModeration({ onUpdate }: ContentModerationProps) {
  const { token } = useUser()
  const toast = useToast()
  const [reports, setReports] = useState<Report[]>([])
  // const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  // const [authError, setAuthError] = useState<any>(null)
  // const [user, setUser] = useState<any>(null)
  // const [rpcError, setRpcError] = useState<any>(null)
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)
  const [showActionModal, setShowActionModal] = useState(false)
  const [filter, setFilter] = useState<'all' | 'pending' | 'under_review' | 'resolved' | 'dismissed'>('all')
  const [actionTaken, setActionTaken] = useState('')
  const [adminNotes, setAdminNotes] = useState('')

  // Load data
  const loadData = async () => {
    setLoading(true)
    try {
      if (!token) {
        setReports([])
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/reports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to load reports')
      }

      setReports(payload?.reports || [])
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load reports')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Update report status
  const updateReportStatus = async (_reportId: string, status: string, _action?: string, _notes?: string) => {
    try {
      if (!token) {
        toast.error('Please log in as an admin')
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/reports/${_reportId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status,
          action_taken: _action,
          admin_notes: _notes
        })
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to update report')
      }

      setShowActionModal(false)
      setActionTaken('')
      setAdminNotes('')
      await loadData()
      onUpdate()
      toast.success('Report updated successfully')
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to update report')
    }
  }

  // Take action on reported content
  const takeActionOnContent = async (report: Report, _action: string) => {
    try {
      if (!token) return
      const apiUrl = getApiUrl()

      // Property actions
      if (report.content_type === 'property' && report.content_id) {
        if (_action === 'hide_property') {
          await fetch(`${apiUrl}/admin/properties/${report.content_id}/verify`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ isVerified: false })
          }).catch(() => undefined)
        }

        if (_action === 'remove_property') {
          await fetch(`${apiUrl}/admin/properties/${report.content_id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          }).catch(() => undefined)
        }
      }

      // User actions (best-effort)
      if (report.content_type === 'user' && report.reported_user_id) {
        if (_action === 'suspend_user' || _action === 'ban_user') {
          await fetch(`${apiUrl}/admin/users/${report.reported_user_id}/status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ isActive: false })
          }).catch(() => undefined)
        }
      }
    } catch (error) {
      console.error('Error taking action:', error)
    }
  }

  // Get report reason color
  const getReportReasonColor = (reason: string) => {
    switch (reason) {
      case 'inappropriate':
        return 'bg-red-100 text-red-800'
      case 'spam':
        return 'bg-yellow-100 text-yellow-800'
      case 'fraud':
        return 'bg-purple-100 text-purple-800'
      case 'harassment':
        return 'bg-orange-100 text-orange-800'
      case 'fake_listing':
        return 'bg-pink-100 text-pink-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-red-100 text-red-800'
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800'
      case 'resolved':
        return 'bg-green-100 text-green-800'
      case 'dismissed':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Filter reports
  const filteredReports = reports.filter(report => {
    if (filter === 'all') return true
    return report.status === filter
  })

  // Calculate stats
  const pendingReports = reports.filter(r => r.status === 'pending').length
  const underReviewReports = reports.filter(r => r.status === 'under_review').length
  const resolvedReports = reports.filter(r => r.status === 'resolved').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Content Moderation</h2>
        <div className="flex space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Reports</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Pending Reports</h3>
          <p className="text-3xl font-bold text-red-600">{pendingReports}</p>
          <p className="text-sm text-gray-500">Require immediate attention</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Under Review</h3>
          <p className="text-3xl font-bold text-yellow-600">{underReviewReports}</p>
          <p className="text-sm text-gray-500">Being investigated</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Resolved</h3>
          <p className="text-3xl font-bold text-green-600">{resolvedReports}</p>
          <p className="text-sm text-gray-500">Action taken</p>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Content Reports</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading reports...</p>
          </div>
        ) : filteredReports.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredReports.map((report) => (
              <div key={report.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-gray-900">
                        {report.content_type.charAt(0).toUpperCase() + report.content_type.slice(1)} Report
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getReportReasonColor(report.report_reason)}`}>
                        {report.report_reason.replace('_', ' ').charAt(0).toUpperCase() + report.report_reason.replace('_', ' ').slice(1)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{report.description}</p>
                    
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>
                        <span className="font-medium">Reporter:</span> {report.reporter?.email || 'Unknown'}
                      </p>
                      <p>
                        <span className="font-medium">Reported User:</span> {report.reported_user?.email || 'Unknown'}
                      </p>
                      <p>
                        <span className="font-medium">Content Type:</span> {report.content_type}
                      </p>
                      <p>
                        <span className="font-medium">Created:</span> {new Date(report.created_at).toLocaleDateString()}
                      </p>
                      {report.property && (
                        <p>
                          <span className="font-medium">Property:</span> {report.property.title}
                        </p>
                      )}
                    </div>

                    {report.evidence && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Evidence:</p>
                        <p className="text-sm text-gray-600">{report.evidence}</p>
                      </div>
                    )}

                    {report.action_taken && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Action Taken:</p>
                        <p className="text-sm text-gray-600">{report.action_taken}</p>
                      </div>
                    )}

                    {report.admin_notes && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Admin Notes:</p>
                        <p className="text-sm text-gray-600">{report.admin_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {report.status === 'pending' && (
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedReport(report)
                        setShowActionModal(true)
                      }}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Review & Take Action
                    </button>
                    <button
                      onClick={() => updateReportStatus(report.id, 'under_review')}
                      className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                    >
                      Mark Under Review
                    </button>
                    <button
                      onClick={() => updateReportStatus(report.id, 'dismissed')}
                      className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
                
                {report.status === 'under_review' && (
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedReport(report)
                        setShowActionModal(true)
                      }}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                    >
                      Take Action
                    </button>
                    <button
                      onClick={() => updateReportStatus(report.id, 'pending')}
                      className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                    >
                      Reopen
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No reports found</p>
            <p className="text-sm">All reports have been processed</p>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showActionModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Take Action on Report</h3>
                <button
                  onClick={() => setShowActionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Report Details</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium">{selectedReport.content_type} Report</p>
                    <p className="text-sm text-gray-600 mt-1">{selectedReport.description}</p>
                    <div className="mt-2 text-sm text-gray-500">
                      <p>Reporter: {selectedReport.reporter?.email}</p>
                      <p>Reported User: {selectedReport.reported_user?.email}</p>
                      <p>Reason: {selectedReport.report_reason}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Action Taken *
                  </label>
                  <select
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select an action</option>
                    {selectedReport.content_type === 'property' && (
                      <>
                        <option value="hide_property">Hide Property</option>
                        <option value="remove_property">Remove Property</option>
                        <option value="warn_landlord">Warn Landlord</option>
                      </>
                    )}
                    {selectedReport.content_type === 'user' && (
                      <>
                        <option value="warn_user">Warn User</option>
                        <option value="suspend_user">Suspend User</option>
                        <option value="ban_user">Ban User</option>
                      </>
                    )}
                    <option value="no_action">No Action Required</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Internal notes for admin reference..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => {
                      if (actionTaken) {
                        // Take action on content
                        if (actionTaken !== 'no_action') {
                          takeActionOnContent(selectedReport, actionTaken)
                        }
                        updateReportStatus(
                          selectedReport.id, 
                          'resolved', 
                          actionTaken, 
                          adminNotes
                        )
                      } else {
                        alert('Please select an action')
                      }
                    }}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Take Action
                  </button>
                  <button
                    onClick={() => updateReportStatus(selectedReport.id, 'dismissed')}
                    className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Dismiss Report
                  </button>
                  <button
                    onClick={() => setShowActionModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 



